import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useUIStore } from "../stores/uiStore";
import css from "./Reveal.module.css";

/** One thing a gated surface needs before it may show. `label` is already
 *  translated — the caller owns the vocabulary of its own requirements. */
export interface Requirement {
  label: string;
  ok: boolean;
}

/**
 * ADR 356 — the diagnostic reveal. A surface gated on capabilities/data renders
 * through this instead of early-returning null: requirements all met → the
 * children (or nothing, for the early-return call style); any missing → null
 * normally, but under Modo diagnóstico a ghost card naming the surface and
 * listing every requirement as met/missing, plus a hover title summarizing
 * what's absent.
 *
 * ADR 227 ("absence is the empty state, no placeholder cards") still governs
 * the operating view — the ghost exists only while an admin has the mode on,
 * which is exactly the moment "why is this hidden?" is the question on screen.
 */
export function Reveal({ label, reqs, children }: {
  label: string;
  reqs: Requirement[];
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const revealMode = useUIStore((s) => s.revealMode);
  if (reqs.every((r) => r.ok)) return <>{children ?? null}</>;
  if (!revealMode) return null;
  const missing = reqs.filter((r) => !r.ok).map((r) => r.label).join(", ");
  return (
    <div className={css.ghost} data-testid="reveal-ghost" title={t("diag.missingTitle", { list: missing })}>
      <span className={css.ghostLabel}>{label}</span>
      <ul className={css.reqs}>
        {reqs.map((r) => (
          <li key={r.label} className={r.ok ? css.met : css.missing}>
            <span className={css.dot} aria-hidden />
            <span className={css.reqLabel}>{r.label}</span>
            <span className={css.state}>{r.ok ? t("diag.met") : t("diag.notMet")}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
